/**
 * @module store/Migrations.js
 * Contains series of migration steps in order to migrate store data
 * https://github.com/wildlifela/redux-persist-migrate
 */
import { List, Map } from 'immutable';

import * as Constants from '../constants';
import { ListenerKeys } from '../constants/Listener';
import { LoginKeys } from '../constants/Login';
import { StationConstants } from '../constants/StationConstants';
import { WINDOW_MIGRATION_KEY } from '../tools/ImmutableMigrationTransform';

export default {
  1: s => {
    function upgradeListenerStationHistory(state) {
      // if initial load of site, no listener to update, just return state as is
      if (!state.listeners) {
        return {
          ...state,
        };
      }
      const listenerIds = state.listeners.get(ListenerKeys.LISTENERS_KEY).keySeq();
      let listeners;

      const newStationHistories = listenerIds.map(listenerId => {
        const stationId = state.listeners.getIn([
          ListenerKeys.LISTENERS_KEY,
          listenerId,
          'stations',
          StationConstants.CURRENT_STATION,
        ]);

        if (!stationId) {
          return List();
        }

        const stationHistory = state.listeners.getIn(
          [
            ListenerKeys.LISTENERS_KEY,
            listenerId,
            'stations',
            StationConstants.STATION_HISTORY_KEY,
          ],
          List(),
        );

        let newStationHistory = List();
        let sessionHistory = stationHistory.get(stationId, List());

        // reverse the order, so we put the oldest track in the station history queue first
        sessionHistory = sessionHistory.reverse();
        sessionHistory.forEach(trackToken => {
          if (newStationHistory.size !== Constants.Playback.SESSION_HISTORY_LIMIT) {
            newStationHistory = newStationHistory.unshift(new Map({ trackToken, stationId }));
          }
        });

        return newStationHistory;
      });

      listeners = state.listeners;
      listenerIds.forEach((listenerId, index) => {
        listeners = listeners.setIn(
          [
            ListenerKeys.LISTENERS_KEY,
            listenerId,
            'stations',
            StationConstants.STATION_HISTORY_KEY,
          ],
          newStationHistories.get(index),
        );
      });

      return {
        ...state,
        listeners,
      };
    }

    // due to a previous bug, there is an 'undefined' key in storage here. This will get rid of it.
    function upgradeStorage(state) {
      // if initial load of site, no storage to update, just return state as is
      if (!state.storage) {
        return {
          ...state,
        };
      }
      return {
        ...state,
        storage: {
          version: s.storage.version,
        },
      };
    }

    const newState = upgradeListenerStationHistory(s);
    return upgradeStorage(newState);
  },
  2: state => {
    if (state.listeners) {
      const listenerId = state.listeners.get(ListenerKeys.CURRENT_USER_ID_KEY);
      const username = state.listeners.getIn([ListenerKeys.LISTENERS_KEY, listenerId, 'username']);
      return {
        ...state,
        listeners: undefined,
        login: state.login.set('username', username),
      };
    }
    return state;
  },
  3: state => {
    if (state.audioAds) {
      const queue = state.audioAds.get('AUDIO_AD_QUEUE');
      const index = 1;
      if (queue) {
        return {
          ...state,
          audioAds: Map({ index, queue }),
        };
      }
      return state;
    }
    return state;
  },
  4: state => {
    // This migration is for upgrading the immutableJS (de)serializer
    // We just add some auth state to the window in the ImmutableMigrationTransform
    // and consume here
    if (window[WINDOW_MIGRATION_KEY]) {
      const { loginAuthToken, username, loginRememberMe } = window[WINDOW_MIGRATION_KEY];
      window[WINDOW_MIGRATION_KEY] = null;
      return {
        ...state,
        login: Map({
          [LoginKeys.LOGIN_AUTH_TOKEN]: loginAuthToken,
          [LoginKeys.USERNAME]: username,
          [LoginKeys.LOGIN_REMEMBER_ME_KEY]: loginRememberMe,
        }),
      };
    }
    return state;
  },
  5: state => {
    if (state.entities && state.entities.get('onDemand')) {
      const entities = state.entities.delete('onDemand');
      return {
        ...state,
        entities,
      };
    }
    return state;
  },
};
